import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Spin } from 'antd'
import SectionTitle from '../elements/SectionTitle'

type TConsentAuthenticationProps = {
  inProgress: boolean
  consentUrl?: string
  message?: string
}

const POPUP_NAME = 'swisscom-consent'
// Swisscom's minimum is 600 x 400.
const POPUP_WIDTH = 600
const POPUP_HEIGHT = 750

const popupFeatures = (): string => {
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - POPUP_WIDTH) / 2))
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2))
  return `popup=yes,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`
}

/**
 * The Swisscom confirmation page (MAB login with collever trust, or the AIS
 * step-up page with the legacy backend) opens in its own window, never in a
 * frame: the MAB login does not render embedded. The window must be opened
 * inside the click handler, or the browser blocks it.
 */
const ConsentAuthentication = ({ inProgress, consentUrl, message }: TConsentAuthenticationProps) => {
  const { t } = useTranslation(['consent'])
  const popup = useRef<Window | null>(null)

  const closePopup = () => {
    try {
      popup.current?.close()
    } catch {
      // The window may already be gone; nothing to do.
    }
    popup.current = null
  }

  // Polling ends with inProgress = false (success, failure, cancel, timeout).
  useEffect(() => {
    if (!inProgress) closePopup()
  }, [inProgress])

  useEffect(() => closePopup, [])

  const openConsent = () => {
    if (!consentUrl) return
    popup.current = window.open(consentUrl, POPUP_NAME, popupFeatures())
    popup.current?.focus()
  }

  return (
    <div>
      {inProgress ? (
        <div className="border-2 border-gray-100 mt-4 p-4 shadow-sm rounded-md">
          <SectionTitle title={t('consent:authentication')}/>
          {!consentUrl ? (
            <>
              <div className="mt-2 mb-4 font-normal text-base text-gray-700">{t('consent:instructions.general')}</div>
              <div style={{ width: '600px', height: '500px' }} className="bg-gray-50 m-auto">
                <div className="flex flex-col justify-between text-center p-8">
                  <p className="text-2xl">{t('consent:instructions.followMobile')}</p>
                  <p className="my-4 ">{message}</p>
                  <div className="pt-8">
                    <Spin spinning={inProgress} size="large" />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center text-center gap-4 p-8 bg-gray-50">
              <p className="text-base text-gray-700">{t('consent:instructions.openSwisscom')}</p>
              <Button type="primary" size="large" onClick={openConsent}>
                {t('consent:action.openSwisscom')}
              </Button>
              <p className="text-sm text-gray-600">
                {t('consent:instructions.popupBlocked')}{' '}
                <a href={consentUrl} target="_blank" rel="noopener">
                  {t('consent:action.openSwisscomTab')}
                </a>
              </p>
              <p className="text-sm text-gray-600">{t('consent:instructions.closeSwisscom')}</p>
              <Spin spinning={inProgress} />
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default ConsentAuthentication
